/**
 * AiCachingAdminPanel.jsx
 *
 * Admin panel for platform-wide prompt caching configuration.
 * Toggle which task categories use Anthropic/Gemini prompt caching.
 * Changes take effect within 60 seconds (in-process TTL cache on backend).
 *
 * Location: Admin → Operations → AI Caching
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Zap, Info, CheckCircle2, XCircle, RefreshCw, Clock } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { cn } from '@/lib/utils';

const TASK_HIGH_VALUE_HINT =
  'High-value — system prompt repeats many times per job. Caching here saves the most.';
const PROVIDER_SUPPORT = {
  'Anthropic Claude (all models)': true,
  'Google Gemini': true,
  'OpenAI (automatic)': false,
};

function ProviderBadge({ name }) {
  const isSupported = PROVIDER_SUPPORT[name] !== false;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
      isSupported
        ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
        : 'border-border bg-muted/30 text-muted-foreground line-through',
    )}>
      {isSupported ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
      {name}
    </span>
  );
}

function TaskRow({ task, onChange }) {
  return (
    <div className={cn(
      'flex items-start gap-4 rounded-lg border p-3.5 transition-colors',
      task.enabled ? 'border-primary/20 bg-primary/3' : 'border-border bg-muted/10',
    )} data-testid={`caching-task-${task.key}`}>
      <Switch
        checked={task.enabled}
        onCheckedChange={(v) => onChange(task.key, v)}
        data-testid={`caching-toggle-${task.key}`}
        className="mt-0.5 shrink-0"
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12.5px] font-semibold text-foreground">{task.label}</span>
          {task.high_value && (
            <Badge className="gap-1 bg-amber-50 border-amber-300 text-amber-800 text-[10px]">
              <Zap className="h-2.5 w-2.5" /> High savings
            </Badge>
          )}
          {task.default && (
            <span className="text-[10px] text-muted-foreground">(default: on)</span>
          )}
        </div>
        <p className="text-[11.5px] text-muted-foreground leading-snug">{task.description}</p>
        <div className="flex flex-wrap gap-1 pt-0.5">
          {(task.supported_providers || []).map((p) => (
            <ProviderBadge key={p} name={p} />
          ))}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <span className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold',
          task.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground',
        )}>
          {task.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
    </div>
  );
}

export default function AiCachingAdminPanel() {
  const [tasks,        setTasks]        = useState([]);
  const [note,         setNote]         = useState('');
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [dirty,        setDirty]        = useState(false);
  // native_cache_ttl — '5m' (Anthropic ephemeral) or '1h' (extended)
  const [cacheTtl,     setCacheTtl]     = useState('1h');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getAiCaching();
      setTasks(data.tasks || []);
      setNote(data.note || '');
      setCacheTtl(data.native_cache_ttl || '1h');
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load caching settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = (key, value) => {
    setTasks((prev) => prev.map((t) => t.key === key ? { ...t, enabled: value } : t));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const settings = Object.fromEntries(tasks.map((t) => [t.key, t.enabled]));
      await adminApi.updateAiCaching({ settings, native_cache_ttl: cacheTtl });
      toast.success('Caching settings saved. Changes take effect within 60 seconds.');
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const allOn  = tasks.every((t) => t.enabled);
  const allOff = tasks.every((t) => !t.enabled);

  const toggleAll = (v) => {
    setTasks((prev) => prev.map((t) => ({ ...t, enabled: v })));
    setDirty(true);
  };

  return (
    <div className="space-y-5" data-testid="ai-caching-admin-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[14px] font-semibold">Prompt Caching — Platform AI Configuration</h2>
          <p className="mt-1 max-w-2xl text-[12px] text-muted-foreground">
            Control which task categories use <strong>Anthropic's prompt caching</strong> (ephemeral 5-min TTL).
            Caching reuses large system prompts across repeated calls, reducing input token costs by up to 90%
            on the cached portion. Only affects platform routes — BYOK users control their own caching separately.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="shrink-0"
          title="Refresh"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 rounded-md border border-sky-200/60 bg-sky-50/80 px-3 py-2">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" />
        <div className="text-[11.5px] text-sky-900 space-y-0.5">
          <p>
            <strong>OpenAI</strong> applies automatic prompt caching silently — no configuration needed.
          </p>
          <p>
            <strong>Anthropic Claude</strong> charges a small cache-write fee per 5-minute window,
            offset by ~90% savings on cache-read tokens. Net benefit is highest for tasks with
            large, repeated system prompts (walkers, reviews, bulk ops).
          </p>
          <p>
            Changes take effect within <strong>60 seconds</strong> across all running instances.
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline" size="sm" className="h-7 text-xs gap-1"
          onClick={() => toggleAll(true)} disabled={allOn}
        >Enable all</Button>
        <Button
          variant="outline" size="sm" className="h-7 text-xs gap-1"
          onClick={() => toggleAll(false)} disabled={allOff}
        >Disable all</Button>

        <span className="h-5 w-px bg-border mx-1" />

        {/* Cache TTL segmented control */}
        <div className="flex items-center gap-1.5" data-testid="cache-ttl-control">
          <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-[12px] font-medium text-foreground">Cache TTL:</span>
          <div className="flex overflow-hidden rounded-md border border-border">
            {[
              { value: '5m', label: '5 min', desc: 'Anthropic ephemeral — cheapest write fee' },
              { value: '1h', label: '1 hour', desc: 'Extended — best for walkers & bulk ops' },
            ].map(({ value, label, desc }) => (
              <button
                key={value}
                type="button"
                onClick={() => { setCacheTtl(value); setDirty(true); }}
                title={desc}
                data-testid={`cache-ttl-${value}`}
                className={cn(
                  'h-7 px-3 text-[12px] font-medium transition-colors',
                  cacheTtl === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-secondary hover:text-foreground',
                  value === '1h' && 'border-l border-border',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="text-[10.5px] text-muted-foreground hidden sm:block">
            {cacheTtl === '5m'
              ? '5-min window — lowest cache-write cost'
              : '1-hour window — ideal for long walker runs'}
          </span>
        </div>

        <div className="flex-1" />
        {dirty && (
          <span className="text-[11px] text-amber-700 font-medium">Unsaved changes</span>
        )}
        <Button
          size="sm" onClick={save} disabled={saving || !dirty}
          className="gap-1.5" data-testid="ai-caching-save-btn"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      {/* Task rows */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading settings…</span>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <TaskRow key={task.key} task={task} onChange={handleToggle} />
          ))}
        </div>
      )}

      {/* Footer note */}
      {note && (
        <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
          {note}
        </p>
      )}

      {/* Limitation note */}
      <div className="rounded-md border border-border bg-muted/15 p-3 text-[11.5px] text-muted-foreground space-y-1">
        <p className="font-semibold text-foreground">Important caveats</p>
        <ul className="space-y-0.5 pl-0 list-disc list-inside">
          <li>Prompt caching adds a one-time cache-write cost. For short or rarely repeated tasks, this may increase costs.</li>
          <li>Cache TTL is configurable: <strong>5 minutes</strong> (Anthropic ephemeral, cheapest write fee) or <strong>1 hour</strong> (extended, best for walker runs). Set above and save.</li>
          <li>Enabling caching for <em>stream</em> tasks increases latency by ~100ms (cache warmup on first call).</li>
          <li>BYOK users have a separate personal toggle in Settings → BYOK Keys → Prompt Caching.</li>
        </ul>
      </div>
    </div>
  );
}
